import React, { useCallback, useState, useEffect } from 'react';
import { Modal, Input, message, Spin, Select, Divider, Typography, Row, Col, Popover } from 'antd';
import apiClient from '../services/apiClient';
import reportService from '../services/reportService';
import settingsService from '../services/settingsService';

// ─── Brand colours ────────────────────────────────────────────────────────────
const C = {
  teal:       '#267a96',
  tealLight:  '#E6F6F8',
  navy:       '#1A3A6B',
  blue:       '#267a96',  // remapped → brand teal
  blueMid:    '#1A3A6B',  // remapped → brand navy
  orange:     '#D95E1E',
  red:        '#D94040',
  redLight:   '#FDEAEA',
  amber:      '#D97706',
  amberLight: '#FEF3DC',
  green:      '#16A34A',
  greenLight: '#E8F7EE',
  border:     '#E5E7EB',
  text:       '#111827',
  textMid:    '#374151',
  textLight:  '#6B7280',
  bg:         '#F9FAFB',
  white:      '#FFFFFF',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const pct = (v, d = 1) => `${Number(v || 0).toFixed(d)}%`;
const num = (v) => Number(v || 0).toLocaleString();

const scoreColor = (v) => {
  const n = Number(v || 0);
  if (n < 60) return C.red;
  if (n < 80) return C.amber;
  return C.green;
};
const scoreLabel = (v) => {
  const n = Number(v || 0);
  if (n < 60) return 'KNOWLEDGE GAP';
  if (n < 80) return 'NEEDS ATTENTION';
  return null;
};
const scoreLabelBg = (v) => {
  const n = Number(v || 0);
  if (n < 60) return C.redLight;
  if (n < 80) return C.amberLight;
  return C.greenLight;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const PageHeader = ({ section, title, subtitle, pageLabel, onTitleChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  const commit = () => {
    const val = draft.trim();
    if (val && val !== title) onTitleChange && onTitleChange(val);
    setEditing(false);
  };

  return (
    <div style={{ marginBottom: 0 }}>
      <div style={{ fontSize: 10, color: C.textLight, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        {section}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {editing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <input
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
                style={{
                  fontSize: 22, fontWeight: 700, color: C.text,
                  border: 'none', borderBottom: `2px solid ${C.teal}`,
                  outline: 'none', background: 'transparent',
                  width: '100%', padding: '2px 0', fontFamily: 'inherit',
                }}
              />
            </div>
          ) : (
            <div
              onClick={() => onTitleChange && (setDraft(title), setEditing(true))}
              title={onTitleChange ? 'Click to edit title' : undefined}
              style={{
                fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 2,
                cursor: onTitleChange ? 'text' : 'default',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              {title}
              {onTitleChange && <span style={{ fontSize: 12, color: C.textLight, fontWeight: 400, opacity: 0.5 }}>✎</span>}
            </div>
          )}
          <div style={{ fontSize: 13, color: C.orange, fontWeight: 600 }}>{subtitle}</div>
        </div>
        {pageLabel && (
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>Page {pageLabel}</div>
        )}
      </div>
      <div style={{ height: 4, background: `linear-gradient(90deg, ${C.teal} 0%, ${C.blueMid} 100%)`, margin: '12px 0 0 0', borderRadius: 2 }} />
    </div>
  );
};

const Section = ({ children, style = {}, id }) => (
  <div id={id} style={{
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...style
  }}>
    {children}
  </div>
);

const KpiCard = ({ value, label, color = C.teal }) => (
  <div style={{
    flex: 1, background: C.white, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: '14px 20px', textAlign: 'center', minWidth: 120,
  }}>
    <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{label}</div>
  </div>
);

const InsightBlock = ({ title, text, dotColor }) => (
  <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
    <div style={{
      width: 32, height: 32, borderRadius: '50%', background: dotColor + '22',
      border: `2px solid ${dotColor}22`, flexShrink: 0, marginTop: 2
    }} />
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: dotColor, letterSpacing: 1, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6 }}>{text}</div>
    </div>
  </div>
);

// ─── Sort toggle pill ─────────────────────────────────────────────────────────
const SortToggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 6, padding: 2, gap: 2 }}>
    {[{ k: 'averageScore', label: 'Score' }, { k: 'completionRate', label: 'Completion' }].map(({ k, label }) => (
      <button key={k} onClick={() => onChange(k)} style={{
        background: value === k ? C.white : 'transparent',
        border: value === k ? `1px solid ${C.border}` : '1px solid transparent',
        borderRadius: 4, padding: '2px 10px', cursor: 'pointer',
        fontSize: 10, fontWeight: value === k ? 700 : 400,
        color: value === k ? C.text : C.textLight,
        boxShadow: value === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.15s',
      }}>{label}</button>
    ))}
  </div>
);

// ─── Org ranked table (dept deep-dive) ────────────────────────────────────────
const DeptDeepDive = ({ deptLabel, deptColor = C.teal, completionRate, avgScore, staffCount, orgs, benchmark = 90 }) => {
  const [sortKey, setSortKey] = useState('averageScore');
  const sorted = [...(orgs || [])].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  return (
    <Section>
      <div style={{
        background: deptColor + '18', borderLeft: `4px solid ${deptColor}`,
        padding: '10px 16px', borderRadius: 4,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: deptColor, letterSpacing: 0.5 }}>{deptLabel}</div>
        <div style={{ display: 'flex', gap: 32 }}>
          {[
            { val: pct(completionRate, 0), lbl: 'Network Completion', color: C.blue },
            { val: pct(avgScore, 0), lbl: 'Network Avg Score', color: C.navy },
            ...(staffCount != null ? [{ val: num(staffCount), lbl: 'Staff on Platform', color: C.teal }] : []),
          ].map(({ val, lbl, color }) => (
            <div key={lbl} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color }}>{val}</div>
              <div style={{ fontSize: 10, color: C.textLight }}>{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend + sort toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          {[
            { el: <div style={{ width: 20, height: 8, background: C.blue, borderRadius: 4 }} />, lbl: 'Completion rate' },
            { el: <div style={{ width: 20, height: 8, background: C.navy, borderRadius: 4 }} />, lbl: 'Average score' },
            { el: <div style={{ borderTop: `2px dashed ${C.red}`, width: 20 }} />, lbl: `Benchmark (${benchmark}%)` },
          ].map(({ el, lbl }) => (
            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{el}<span style={{ fontSize: 10, color: C.textLight }}>{lbl}</span></div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: C.textLight }}>Rank by</span>
          <SortToggle value={sortKey} onChange={setSortKey} />
        </div>
      </div>

      {/* Table header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '32px 1fr 280px 72px 60px',
        gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`,
        fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {['#', 'Dealership', 'Rates', 'Comp%', 'Score%'].map((h, i) => {
          const isComp = h === 'Comp%';
          const isScore = h === 'Score%';
          const isActive = (isComp && sortKey === 'completionRate') || (isScore && sortKey === 'averageScore');
          return (
            <div key={h} style={{ textAlign: i >= 3 ? 'right' : 'left', color: isActive ? C.teal : C.textLight }}>
              {h}{isActive ? ' ▼' : ''}
            </div>
          );
        })}
      </div>

      {sorted.map((org, i) => {
        const rank = i + 1;
        const isTop = rank <= 3;
        const cr = Number(org.completionRate || 0);
        const sc = Number(org.averageScore || 0);
        const isLow = rank > sorted.length - 3 && sorted.length > 6 && (sortKey === 'completionRate' ? cr : sc) < 30;
        return (
          <div key={org.organization || i} style={{
            display: 'grid', gridTemplateColumns: '32px 1fr 280px 72px 60px',
            gap: 8, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
            background: isTop ? C.greenLight : isLow ? C.redLight : 'transparent', borderRadius: 4,
          }}>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: isTop ? C.green : C.textLight, textAlign: 'center' }}>{rank}</div>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: C.text }}>{org.organization || '—'}</div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, marginBottom: 3, position: 'relative', overflow: 'visible' }}>
                <div style={{ width: `${Math.min(cr, 100)}%`, height: '100%', background: C.blue, borderRadius: 5 }} />
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${benchmark}%`, width: 2, background: C.red }} />
              </div>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: C.navy, borderRadius: 5 }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'completionRate' ? 700 : 400, color: sortKey === 'completionRate' ? C.teal : C.textMid }}>{pct(cr, 0)}</div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'averageScore' ? 700 : 400, color: sortKey === 'averageScore' ? C.teal : C.textMid }}>{pct(sc, 0)}</div>
          </div>
        );
      })}
      {sorted.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: C.textLight, fontSize: 12 }}>No data available</div>}
    </Section>
  );
};

// ─── Question analysis ─────────────────────────────────────────────────────────
const QuestionAnalysis = ({ deptLabel, deptColor = C.teal, questions: qs = [] }) => {
  const sorted = [...qs].sort((a, b) => (a.successRate || 0) - (b.successRate || 0));
  const gaps = sorted.filter(q => (q.successRate || 0) < 60).length;
  const attention = sorted.filter(q => (q.successRate || 0) >= 60 && (q.successRate || 0) < 80).length;
  const strong = sorted.filter(q => (q.successRate || 0) >= 80).length;

  return (
    <Section>
      <div style={{
        background: deptColor + '18', borderLeft: `4px solid ${deptColor}`,
        padding: '10px 16px', borderRadius: 4,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: deptColor, letterSpacing: 0.5 }}>{deptLabel}</div>
        <div style={{ fontSize: 11, color: C.textLight }}>{sorted.length} questions · Ranked lowest to highest</div>
      </div>

      {/* Knowledge threshold legend */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: C.textLight, fontWeight: 600 }}>KNOWLEDGE THRESHOLD:</span>
        {[
          { color: C.red, label: 'Below 60% — Knowledge gap' },
          { color: C.amber, label: '60%–79% — Needs attention' },
          { color: C.green, label: '80% and above — Strong' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
            <span style={{ fontSize: 10, color: C.textLight }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Column headers */}
      <div style={{
        display: 'grid', gridTemplateColumns: '32px 1fr 220px 52px',
        gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`,
        fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {['#', 'Question', '% Correct', 'Result'].map((h, i) => (
          <div key={h} style={i === 3 ? { textAlign: 'right' } : {}}>{h}</div>
        ))}
      </div>

      {sorted.map((q, i) => {
        const sc = Number(q.successRate || 0);
        const color = scoreColor(sc);
        const label = scoreLabel(sc);
        const labelBg = scoreLabelBg(sc);
        return (
          <div key={q.questionId || i} style={{
            display: 'grid', gridTemplateColumns: '32px 1fr 220px 52px',
            gap: 8, padding: '8px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
          }}>
            <div style={{ fontSize: 11, color: C.textLight, textAlign: 'center' }}>{i + 1}</div>
            <div>
              <div style={{ fontSize: 11, color: C.text, lineHeight: 1.4, marginBottom: 2 }}>{q.questionText || '—'}</div>
              {(q.campaign || label) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  {q.campaign && <span style={{ fontSize: 9, color: C.textLight }}>{q.campaign}</span>}
                  {label && (
                    <span style={{ fontSize: 9, fontWeight: 700, color, background: labelBg, borderRadius: 3, padding: '1px 5px', letterSpacing: 0.5 }}>{label}</span>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 10, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: color, borderRadius: 5 }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color }}>{pct(sc, 0)}</div>
          </div>
        );
      })}

      {sorted.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: C.textLight, fontSize: 12 }}>No question data available</div>}

      {sorted.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
          {[
            { count: gaps, label: 'Knowledge gaps', sublabel: '(below 60%)', bg: C.redLight, color: C.red },
            { count: attention, label: 'Needs attention', sublabel: '(60%–79%)', bg: C.amberLight, color: C.amber },
            { count: strong, label: 'Strong knowledge', sublabel: '(80% and above)', bg: C.greenLight, color: C.green },
          ].map(({ count, label, sublabel, bg, color }) => (
            <div key={label} style={{ flex: 1, textAlign: 'center', background: bg, borderRadius: 6, padding: '14px 8px' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{count}</div>
              <div style={{ fontSize: 11, color, fontWeight: 600, marginTop: 4 }}>{label}</div>
              <div style={{ fontSize: 10, color: color + 'AA' }}>{sublabel}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ─── Executive summary ─────────────────────────────────────────────────────────
const ExecSummary = ({ deptLabel, deptColor = C.teal, completionRate, avgScore, knowledgeGaps, month }) => (
  <Section>
    <div style={{
      background: deptColor + '18', borderLeft: `4px solid ${deptColor}`,
      padding: '10px 16px', borderRadius: 4,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
    }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: deptColor, letterSpacing: 0.5 }}>{deptLabel}</div>
      {month && <div style={{ fontSize: 11, color: C.textLight }}>Strategic interpretation — {month}</div>}
    </div>
    <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
      <KpiCard value={pct(completionRate, 0)} label="Network completion rate" color={C.teal} />
      <KpiCard value={pct(avgScore, 0)} label="Network average score" color={C.navy} />
      <KpiCard value={String(knowledgeGaps || 0)} label="Knowledge gaps identified" color={C.red} />
    </div>
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Prepared by TapLearnGo</div>
        <div style={{ fontSize: 11, color: C.textLight }}>Learning &amp; Performance Analytics</div>
      </div>
      <div style={{ fontSize: 11, color: C.textLight }}>{new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
    </div>
  </Section>
);

// ─── Network overview ranked table ─────────────────────────────────────────────
const NetworkSummary = ({ overview, orgs, departmentGroups = [], deptLabelPlural = 'department' }) => {
  const [sortKey, setSortKey] = useState('averageScore');
  const completionRate = overview.completionRate || 0;
  const avgScore = overview.averageScore || 0;
  const totalParticipants = overview.uniqueEmployees || overview.totalTargets || 0;
  const completed = overview.completed || 0;
  const targetDelivery = overview.targetDelivery || {};
  const deliveryRate = overview.totalChannelSuccessRateDisplay || targetDelivery.deliveryRate || `${Number(overview.totalChannelSuccessRate || 0).toFixed(2)}%`;
  const attempted = targetDelivery.attempted || 0;
  const delivered = targetDelivery.delivered || 0;
  const sorted = [...(orgs || [])].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));

  // Colours for dept group cards (cycle through brand palette)
  const deptCardColors = [C.teal, C.navy, C.orange, '#007A88', '#2D527C', '#B85C1A'];

  return (
    <Section id="mc-kpis">
      <div style={{
        background: C.teal + '18', borderLeft: `4px solid ${C.teal}`,
        padding: '10px 16px', borderRadius: 4,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: C.teal, letterSpacing: 0.5 }}>NETWORK OVERVIEW</div>
        <div style={{ display: 'flex', gap: 32 }}>
          {[
            { val: pct(completionRate, 0), lbl: 'Network Completion', color: C.blue },
            { val: pct(avgScore, 0), lbl: 'Network Avg Score', color: C.navy },
            { val: num(totalParticipants), lbl: 'Staff on Platform', color: C.teal },
            { val: deliveryRate, lbl: 'Delivery Rate', color: C.green },
          ].map(({ val, lbl, color }) => (
            <div key={lbl} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color }}>{val}</div>
              <div style={{ fontSize: 10, color: C.textLight }}>{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <KpiCard value={pct(completionRate, 0)} label="Group completion rate" color={C.blue} />
        <KpiCard value={pct(avgScore, 0)} label="Group average score" color={C.navy} />
        <KpiCard value={deliveryRate} label={`Delivery rate (${num(delivered)}/${num(attempted)})`} color={C.teal} />
      </div>

      {/* Department group summary cards */}
      {departmentGroups.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
            Performance by {deptLabelPlural.toLowerCase()} group
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            {departmentGroups.map((dg, idx) => {
              const color = deptCardColors[idx % deptCardColors.length];
              const cr = Number(dg.completionRate || 0);
              const sc = Number(dg.averageScore || 0);
              return (
                <div key={dg.key || dg.id} style={{
                  flex: '1 1 160px', minWidth: 150, border: `1px solid ${C.border}`,
                  borderRadius: 8, overflow: 'hidden', background: C.white,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  {/* Card header */}
                  <div style={{ background: color, padding: '8px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.white, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                      {dg.label || dg.key}
                    </div>
                  </div>
                  {/* Completion rate */}
                  <div style={{ padding: '12px 12px 4px' }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: scoreColor(cr), textAlign: 'center', lineHeight: 1 }}>
                      {pct(cr, 0)}
                    </div>
                    <div style={{ fontSize: 9, color: C.textLight, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Group Completion Rate</div>
                    <div style={{ height: 5, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(cr, 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
                    </div>
                  </div>
                  {/* Avg score */}
                  <div style={{ padding: '10px 12px 14px' }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: scoreColor(sc), textAlign: 'center', lineHeight: 1 }}>
                      {pct(sc, 0)}
                    </div>
                    <div style={{ fontSize: 9, color: C.textLight, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Group Avg Score</div>
                    <div style={{ height: 5, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: color + 'BB', borderRadius: 3 }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {/* Legend + sort toggle */}
      <div id="mc-leaderboard" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          {[
            { el: <div style={{ width: 20, height: 8, background: C.blue, borderRadius: 4 }} />, lbl: 'Completion rate' },
            { el: <div style={{ width: 20, height: 8, background: C.navy, borderRadius: 4 }} />, lbl: 'Average score' },
            { el: <div style={{ borderTop: `2px dashed ${C.red}`, width: 20 }} />, lbl: 'Benchmark (90%)' },
          ].map(({ el, lbl }) => (
            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{el}<span style={{ fontSize: 10, color: C.textLight }}>{lbl}</span></div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: C.textLight }}>Rank by</span>
          <SortToggle value={sortKey} onChange={setSortKey} />
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '32px 1fr 260px 72px 60px',
        gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`,
        fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {['#', 'Dealership', 'Rates', 'Comp%', 'Score%'].map((h, i) => {
          const isComp = h === 'Comp%';
          const isScore = h === 'Score%';
          const isActive = (isComp && sortKey === 'completionRate') || (isScore && sortKey === 'averageScore');
          return (
            <div key={h} style={{ textAlign: i >= 3 ? 'right' : 'left', color: isActive ? C.teal : C.textLight }}>
              {h}{isActive ? ' ▼' : ''}
            </div>
          );
        })}
      </div>

      {sorted.map((org, i) => {
        const rank = i + 1;
        const isTop = rank <= 3;
        const cr = Number(org.completionRate || 0);
        const sc = Number(org.averageScore || 0);
        const isLow = rank > sorted.length - 3 && sorted.length > 6 && (sortKey === 'completionRate' ? cr : sc) < 30;
        return (
          <div key={org.organization || i} style={{
            display: 'grid', gridTemplateColumns: '32px 1fr 260px 72px 60px',
            gap: 8, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
            background: isTop ? C.greenLight : isLow ? C.redLight : 'transparent', borderRadius: 4,
          }}>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: isTop ? C.green : C.textLight, textAlign: 'center' }}>{rank}</div>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: C.text }}>{org.organization || '—'}</div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, marginBottom: 3, position: 'relative', overflow: 'visible' }}>
                <div style={{ width: `${Math.min(cr, 100)}%`, height: '100%', background: C.blue, borderRadius: 5 }} />
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: '90%', width: 2, background: C.red }} />
              </div>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: C.navy, borderRadius: 5 }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'completionRate' ? 700 : 400, color: sortKey === 'completionRate' ? C.teal : C.textMid }}>{pct(cr, 0)}</div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'averageScore' ? 700 : 400, color: sortKey === 'averageScore' ? C.teal : C.textMid }}>{pct(sc, 0)}</div>
          </div>
        );
      })}
    </Section>
  );
};

// ─── 3-month performance trend (one table, month groups with 2 sub-cols each) ──────
const PerformanceTrend = ({ deptLabel, deptColor = C.teal, rows = [], months = [], itemLabel = 'Department', hideLegend = false }) => {
  const [m2, m1, cur] = months;
  const cellColor = (v) => {
    const n = Number(v);
    if (v === null || v === undefined || isNaN(n)) return { bg: '#F9FAFB', text: C.textLight };
    if (n < 60) return { bg: C.redLight, text: C.red };
    if (n < 80) return { bg: C.amberLight, text: C.amber };
    return { bg: C.greenLight, text: C.green };
  };
  const fmtVal = (v) => (v === null || v === undefined || isNaN(Number(v))) ? '—' : `${Number(v).toFixed(0)}%`;
  const trendArrow = (v1, v2) => {
    const diff = Number(v2) - Number(v1);
    if (isNaN(diff) || Math.abs(diff) < 0.01) return { sym: '→', color: C.textLight };
    return diff > 0 ? { sym: '↑', color: C.green } : { sym: '↓', color: C.red };
  };
  // grid: dept name | 2 sub-cols x 3 months | trend
  const grid = '1fr 80px 80px 80px 80px 80px 80px 44px';
  const subHdrStyle = { textAlign: 'center', fontSize: 9, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.3 };
  return (
    <Section>
      <div style={{
        background: deptColor + '18', borderLeft: `4px solid ${deptColor}`,
        padding: '10px 16px', borderRadius: 4,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: deptColor, letterSpacing: 0.5 }}>{deptLabel}</div>
        {!hideLegend && (
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 9, height: 9, borderRadius: 2, background: C.blue }} /><span style={{ fontSize: 10, color: C.textLight }}>Completion</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 9, height: 9, borderRadius: 2, background: C.navy }} /><span style={{ fontSize: 10, color: C.textLight }}>Avg score</span></div>
          </div>
        )}
      </div>

      {/* Month group headers */}
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 4, marginBottom: 0 }}>
        <div />
        {[m2, m1, cur].map((lbl) => (
          <div key={lbl} style={{ gridColumn: 'span 2', textAlign: 'center', fontSize: 10, fontWeight: 700, color: C.navy, background: C.tealLight, borderRadius: '4px 4px 0 0', padding: '4px 0', letterSpacing: 0.3 }}>
            {lbl || '—'}
          </div>
        ))}
        <div />
      </div>

      {/* Sub-column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 4, padding: '4px 0', borderBottom: `2px solid ${C.border}`, marginBottom: 2 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>{itemLabel}</div>
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            <div style={{ ...subHdrStyle, color: C.blue }}>Comp%</div>
            <div style={{ ...subHdrStyle, color: C.navy }}>Score%</div>
          </React.Fragment>
        ))}
        <div style={{ ...subHdrStyle, textAlign: 'center' }}>Trend</div>
      </div>

      {rows.map((row) => {
        const t = trendArrow(row.m1cr, row.curcr);
        return (
          <div key={row.name} style={{ display: 'grid', gridTemplateColumns: grid, gap: 4, padding: '6px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{row.name}</div>
            {[
              { cr: row.m2cr, sc: row.m2sc },
              { cr: row.m1cr, sc: row.m1sc },
              { cr: row.curcr, sc: row.cursc },
            ].map(({ cr, sc }, i) => {
              const cCr = cellColor(cr); const cSc = cellColor(sc);
              return (
                <React.Fragment key={i}>
                  <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: cCr.text, background: cCr.bg, borderRadius: 4, padding: '4px 2px' }}>{fmtVal(cr)}</div>
                  <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: cSc.text, background: cSc.bg, borderRadius: 4, padding: '4px 2px' }}>{fmtVal(sc)}</div>
                </React.Fragment>
              );
            })}
            <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 700, color: t.color }}>{t.sym}</div>
          </div>
        );
      })}
      {rows.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: C.textLight, fontSize: 12 }}>No trend data available</div>}
    </Section>
  );
};


// ─── Department-level drilldown table (within a group) ───────────────────────
const DeptDrilldown = ({ deptLabel, deptColor = C.teal, depts = [], benchmark = 90, itemLabel = 'department', itemLabelPlural = 'departments' }) => {
  const [sortKey, setSortKey] = useState('averageScore');
  const sorted = [...depts].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  return (
    <Section>
      <div style={{
        background: deptColor + '18', borderLeft: `4px solid ${deptColor}`,
        padding: '10px 16px', borderRadius: 4,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: deptColor, letterSpacing: 0.5 }}>{deptLabel}</div>
        <div style={{ fontSize: 11, color: C.textLight }}>{sorted.length} {sorted.length !== 1 ? itemLabelPlural : itemLabel}</div>
      </div>

      {/* Legend + sort toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          {[
            { el: <div style={{ width: 20, height: 8, background: C.blue, borderRadius: 4 }} />, lbl: 'Completion rate' },
            { el: <div style={{ width: 20, height: 8, background: C.navy, borderRadius: 4 }} />, lbl: 'Average score' },
            { el: <div style={{ borderTop: `2px dashed ${C.red}`, width: 20 }} />, lbl: `Benchmark (${benchmark}%)` },
          ].map(({ el, lbl }) => (
            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{el}<span style={{ fontSize: 10, color: C.textLight }}>{lbl}</span></div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: C.textLight }}>Rank by</span>
          <SortToggle value={sortKey} onChange={setSortKey} />
        </div>
      </div>

      {/* Table header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '32px 1fr 140px 260px 72px 60px',
        gap: 8, padding: '6px 0', borderBottom: `2px solid ${C.border}`,
        fontSize: 10, fontWeight: 600, color: C.textLight, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        {['#', itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1), 'Organisation', 'Rates', 'Comp%', 'Score%'].map((h, i) => {
          const isComp = h === 'Comp%';
          const isScore = h === 'Score%';
          const isActive = (isComp && sortKey === 'completionRate') || (isScore && sortKey === 'averageScore');
          return (
            <div key={h} style={{ textAlign: i >= 4 ? 'right' : 'left', color: isActive ? C.teal : C.textLight }}>
              {h}{isActive ? ' ▼' : ''}
            </div>
          );
        })}
      </div>

      {sorted.map((dept, i) => {
        const rank = i + 1;
        const isTop = rank <= 3;
        const cr = Number(dept.completionRate || 0);
        const sc = Number(dept.averageScore || 0);
        const isLow = rank > sorted.length - 3 && sorted.length > 6 && (sortKey === 'completionRate' ? cr : sc) < 30;
        return (
          <div key={`${dept.department}-${dept.organizationName || i}`} style={{
            display: 'grid', gridTemplateColumns: '32px 1fr 140px 260px 72px 60px',
            gap: 8, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
            background: isTop ? C.greenLight : isLow ? C.redLight : 'transparent', borderRadius: 4,
          }}>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: isTop ? C.green : C.textLight, textAlign: 'center' }}>{rank}</div>
            <div style={{ fontSize: 12, fontWeight: isTop ? 700 : 400, color: C.text }}>{dept.department || '—'}</div>
            <div style={{ fontSize: 11, color: C.textLight }}>{dept.organizationName || '—'}</div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, marginBottom: 3, position: 'relative', overflow: 'visible' }}>
                <div style={{ width: `${Math.min(cr, 100)}%`, height: '100%', background: C.blue, borderRadius: 5 }} />
                <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${benchmark}%`, width: 2, background: C.red }} />
              </div>
              <div style={{ height: 9, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(sc, 100)}%`, height: '100%', background: C.navy, borderRadius: 5 }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'completionRate' ? 700 : 400, color: sortKey === 'completionRate' ? C.teal : C.textMid }}>{pct(cr, 0)}</div>
            <div style={{ textAlign: 'right', fontSize: 11, fontWeight: sortKey === 'averageScore' ? 700 : 400, color: sortKey === 'averageScore' ? C.teal : C.textMid }}>{pct(sc, 0)}</div>
          </div>
        );
      })}
      {sorted.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: C.textLight, fontSize: 12 }}>No {itemLabelPlural.toLowerCase()} data available</div>}
    </Section>
  );
};

// ─── Strategic Summary — driven by parent aiInsights state ───────────────────
const StrategicSummary = ({
  deptLabel, deptColor = C.teal, month,
  insight, loading, err,
  onInsightChange,
  onGenerate,
}) => {
  // Local draft state for inline editing
  const [editing, setEditing] = useState(null); // 'strategic_summary' | 'overall_trend_insight' | number (focus area index)
  const [draft, setDraft] = useState('');

  const startEdit = (field, value) => { setEditing(field); setDraft(value || ''); };
  const commitEdit = (field) => {
    if (!insight) return;
    let updated;
    if (typeof field === 'number') {
      const areas = [...(insight.recommended_focus_areas || [])];
      areas[field] = draft;
      updated = { ...insight, recommended_focus_areas: areas };
    } else {
      updated = { ...insight, [field]: draft };
    }
    onInsightChange && onInsightChange(updated);
    setEditing(null);
  };
  const cancelEdit = () => setEditing(null);

  // Editable text field
  const EditableField = ({ fieldKey, value }) => {
    const isEdit = editing === fieldKey;
    return isEdit ? (
      <div>
        <textarea
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={4}
          style={{
            width: '100%', boxSizing: 'border-box', fontSize: 12, lineHeight: 1.7,
            padding: '10px 12px', borderRadius: 6, border: `2px solid ${deptColor}`,
            resize: 'vertical', fontFamily: 'inherit', color: C.text,
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
          <button onClick={() => commitEdit(fieldKey)} style={{ background: deptColor, color: C.white, border: 'none', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓ Save</button>
          <button onClick={cancelEdit} style={{ background: 'transparent', color: C.textLight, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
        </div>
      </div>
    ) : (
      <div
        onClick={() => insight && startEdit(fieldKey, value)}
        title={insight ? 'Click to edit' : ''}
        style={{
          background: C.tealLight, border: `1px solid ${C.teal}22`, borderRadius: 6,
          padding: '14px 16px', fontSize: 12, color: C.text, lineHeight: 1.7,
          cursor: insight ? 'text' : 'default',
          position: 'relative',
        }}
      >
        {value}
        {insight && <span style={{ position: 'absolute', top: 8, right: 10, fontSize: 10, color: C.textLight, opacity: 0.6 }}>✎</span>}
      </div>
    );
  };

  // Editable focus area item
  const EditableFocusArea = ({ index, value }) => {
    const isEdit = editing === index;
    return isEdit ? (
      <div>
        <textarea
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', fontSize: 12, lineHeight: 1.6,
            padding: '8px 12px', borderRadius: 6, border: `2px solid ${deptColor}`,
            resize: 'vertical', fontFamily: 'inherit', color: C.text,
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
          <button onClick={() => commitEdit(index)} style={{ background: deptColor, color: C.white, border: 'none', borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✓ Save</button>
          <button onClick={cancelEdit} style={{ background: 'transparent', color: C.textLight, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 14px', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
        </div>
      </div>
    ) : (
      <div
        onClick={() => insight && startEdit(index, value)}
        title={insight ? 'Click to edit' : ''}
        style={{
          background: C.white, border: `1px solid ${C.border}`,
          borderLeft: `4px solid ${deptColor}`, borderRadius: '0 6px 6px 0',
          padding: '10px 14px', fontSize: 12, color: C.text, lineHeight: 1.6,
          cursor: insight ? 'text' : 'default', position: 'relative',
        }}
      >
        <span style={{ fontWeight: 700, color: deptColor, marginRight: 6 }}>{index + 1}.</span>{value}
        {insight && <span style={{ position: 'absolute', top: 8, right: 10, fontSize: 10, color: C.textLight, opacity: 0.6 }}>✎</span>}
      </div>
    );
  };

  const PlaceholderBox = ({ text }) => (
    <div style={{
      background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 6, padding: '20px 16px',
      color: C.textLight, fontSize: 12, fontStyle: 'italic', textAlign: 'center',
    }}>{text}</div>
  );

  return (
    <Section>
      <div style={{
        background: deptColor + '18', borderLeft: `4px solid ${deptColor}`,
        padding: '10px 16px', borderRadius: 4,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: deptColor, letterSpacing: 0.5 }}>STRATEGIC SUMMARY — {deptLabel}</div>
        {month && <div style={{ fontSize: 11, color: C.textLight }}>{month}</div>}
      </div>

      {!insight && !loading && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onGenerate} style={{
            background: deptColor, color: C.white, border: 'none', borderRadius: 5,
            padding: '7px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>
            ✦ Generate Insights for this Section
          </button>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
          <div style={{ marginTop: 10, fontSize: 12, color: C.textLight }}>Analysing {deptLabel} data...</div>
        </div>
      )}

      {err && (
        <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{err}</div>
      )}

      {/* Overall Trend Insight */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Overall Trend Insight</div>
        {insight?.overall_trend_insight
          ? <EditableField fieldKey="overall_trend_insight" value={insight.overall_trend_insight} />
          : <PlaceholderBox text={loading ? 'Analysing...' : 'Click “Generate AI Insights” to populate this section'} />}
      </div>

      {/* Strategic Summary */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Strategic Summary</div>
        {insight?.strategic_summary
          ? <EditableField fieldKey="strategic_summary" value={insight.strategic_summary} />
          : <PlaceholderBox text={loading ? 'Analysing...' : 'Click “Generate AI Insights” to populate this section'} />}
      </div>

      {/* Recommended Focus Areas */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 }}>Recommended Focus Areas</div>
        {insight?.recommended_focus_areas?.length
          ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {insight.recommended_focus_areas.map((area, i) => (
                <EditableFocusArea key={i} index={i} value={area} />
              ))}
            </div>
          )
          : <PlaceholderBox text={loading ? 'Analysing...' : 'Click “Generate AI Insights” to populate this section'} />}
      </div>

      {insight && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button onClick={onGenerate} style={{
            background: 'transparent', color: deptColor, border: `1px solid ${deptColor}`,
            borderRadius: 5, padding: '5px 14px', cursor: 'pointer', fontSize: 11,
          }}>
            Regenerate
          </button>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Prepared by TapLearnGo</div>
          <div style={{ fontSize: 11, color: C.textLight }}>Learning &amp; Performance Analytics</div>
        </div>
        <div style={{ fontSize: 11, color: C.textLight }}>{new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      </div>
    </Section>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const MultiCampaignReport = ({ 
  aggregates, 
  loading, 
  error,
  formatPercentDisplay,
  formatNumberDisplay,
  percentValue,
  reportName: defaultReportName = 'MBSA Quiz Analysis',
  savedReportId: initialSavedReportId = null,
  initialAiInsights = null,
  // PDF customisation defaults (passed from ReportViewer via sessionStorage)
  reportLabel: defaultPdfLabel = 'National Performance Report',
  networkName: defaultNetworkName = '',
  orgLabel: defaultOrgLabel = 'Dealership',
  orgLabelPlural: defaultOrgLabelPlural = '',
  deptLabel: defaultDeptLabel = 'Department',
  deptLabelPlural: defaultDeptLabelPlural = 'Departments',
  clientLogoUrl: defaultClientLogoUrl = '',
}) => {
  // Helpers and export (defined before any early returns to satisfy Hooks rules)
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [reportName, setReportName] = useState(defaultReportName);
  const [exporting, setExporting] = useState(false);
  // keyed by dept group key/label: { insight, loading, err }
  const [aiInsights, setAiInsights] = useState({});
  const [aiAllLoading, setAiAllLoading] = useState(false);
  // Save state
  const [savedReportId, setSavedReportId] = useState(initialSavedReportId);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error

  // PDF customisation state
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfLabel, setPdfLabel] = useState(defaultPdfLabel);
  const [pdfNetworkName, setPdfNetworkName] = useState(defaultNetworkName);
  const [pdfOrgLabel, setPdfOrgLabel] = useState(defaultOrgLabel);
  const [pdfOrgLabelPlural, setPdfOrgLabelPlural] = useState(defaultOrgLabelPlural || `${defaultOrgLabel}s`);
  const [pdfDeptLabel, setPdfDeptLabel] = useState(defaultDeptLabel || 'Department');
  const [pdfDeptLabelPlural, setPdfDeptLabelPlural] = useState(defaultDeptLabelPlural || `${defaultDeptLabel || 'Department'}s`);
  const [pdfClientLogoUrl, setPdfClientLogoUrl] = useState(defaultClientLogoUrl);
  const [pdfProfiles, setPdfProfiles] = useState([]);
  const [pdfProfilesLoading, setPdfProfilesLoading] = useState(false);
  const [pdfProfileName, setPdfProfileName] = useState('');
  const [savePdfProfileOpen, setSavePdfProfileOpen] = useState(false);

  // Load PDF profiles from DB on mount
  useEffect(() => {
    setPdfProfilesLoading(true);
    settingsService.getPdfProfiles().then(result => {
      if (result.success) setPdfProfiles(result.data || []);
      setPdfProfilesLoading(false);
    });
  }, []);

  // Update report name when prop changes
  useEffect(() => {
    setReportName(defaultReportName);
  }, [defaultReportName]);

  // Sync PDF customisation defaults from props
  useEffect(() => {
    setPdfLabel(defaultPdfLabel || 'National Performance Report');
    setPdfNetworkName(defaultNetworkName || '');
    const lbl = defaultOrgLabel || 'Dealership';
    setPdfOrgLabel(lbl);
    setPdfOrgLabelPlural(defaultOrgLabelPlural || `${lbl}s`);
    setPdfClientLogoUrl(defaultClientLogoUrl || '');
  }, [defaultPdfLabel, defaultNetworkName, defaultOrgLabel, defaultOrgLabelPlural, defaultClientLogoUrl]);

  // Seed AI insights from saved report when opened from saved list
  useEffect(() => {
    if (initialAiInsights && Object.keys(initialAiInsights).length > 0) {
      const seeded = {};
      Object.entries(initialAiInsights).forEach(([key, ins]) => {
        seeded[key] = { insight: ins, loading: false, err: null };
      });
      setAiInsights(seeded);
    }
  }, [initialAiInsights]);

  // Save / update report
  const handleSaveReport = async () => {
    setSaveStatus('saving');
    const insightsPayload = {};
    Object.entries(aiInsights).forEach(([key, val]) => {
      if (val.insight) insightsPayload[key] = val.insight;
    });
    const overview = aggregates?.overview || {};
    const payload = {
      title: reportName,
      managingGroup: overview.managingGroup || '',
      reportMonth: overview.reportMonth || new Date().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' }),
      reportData: aggregates,
      aiInsights: insightsPayload,
    };
    let result;
    if (savedReportId) {
      result = await reportService.updateSavedReport(savedReportId, { title: reportName, aiInsights: insightsPayload });
    } else {
      result = await reportService.saveReport(payload);
      if (result.success && result.data?._id) setSavedReportId(result.data._id);
    }
    setSaveStatus(result.success ? 'saved' : 'error');
    if (result.success) setTimeout(() => setSaveStatus('idle'), 3000);
  };

  // Update a single dept group insight when user edits inline
  const handleInsightChange = (dgKey, newInsight) => {
    setAiInsights(prev => ({ ...prev, [dgKey]: { ...prev[dgKey], insight: newInsight } }));
    setSaveStatus('idle');
  };

  const exportDocx = useCallback(async (reportTitle) => {
    try {
      const safeAggregates = aggregates || {};
      const overviewData = safeAggregates.overview || {};
      const organizationsData = Array.isArray(safeAggregates.organizations) ? safeAggregates.organizations : [];
      const departmentGroupsData = Array.isArray(safeAggregates.departmentGroups) ? safeAggregates.departmentGroups : [];
      const departmentsData = Array.isArray(safeAggregates.departments) ? safeAggregates.departments : [];
      const groupByOrgStatsData = Array.isArray(safeAggregates.groupByOrgStats) ? safeAggregates.groupByOrgStats : [];
      const groupByDeptStatsData = Array.isArray(safeAggregates.groupByDeptStats) ? safeAggregates.groupByDeptStats : [];
      const orgByGroupStatsData = Array.isArray(safeAggregates.orgByGroupStats) ? safeAggregates.orgByGroupStats : [];
      const groupAnalysisData = Array.isArray(safeAggregates.groupAnalysis) ? safeAggregates.groupAnalysis : [];
      const questionsByDeptGroupData = Array.isArray(safeAggregates.questionsByDeptGroup) ? safeAggregates.questionsByDeptGroup : [];
      const deptGroupQuestionStatsData = Array.isArray(safeAggregates.deptGroupQuestionStats) ? safeAggregates.deptGroupQuestionStats : [];
      const departmentComparisonData = safeAggregates.departmentComparison || null;

      const statusItems = [
        { label: 'Completed', value: overviewData.completed || 0 },
        { label: 'In Progress', value: overviewData.inProgress || 0 },
        { label: 'Pending', value: overviewData.pending || 0 }
      ].filter(item => (item.value || 0) > 0);

      const statusChart = {
        labels: statusItems.map(item => item.label),
        datasets: statusItems.length ? [{
          label: 'Participants',
          data: statusItems.map(item => item.value || 0),
          dataLabels: statusItems.map(item => formatNumberDisplay(item.value || 0))
        }] : []
      };

      const topOrganizations = organizationsData.slice(0, 10);
      const organizationsChart = {
        labels: topOrganizations.map(org => org.organization || 'Unknown'),
        datasets: [
          {
            label: 'Completion Rate',
            data: topOrganizations.map(org => percentValue(org.completionRate)),
            dataLabels: topOrganizations.map(org => org.completionRateDisplay || formatPercentDisplay(org.completionRate || 0))
          },
          {
            label: 'Average Score',
            data: topOrganizations.map(org => percentValue(org.averageScore)),
            dataLabels: topOrganizations.map(org => org.averageScoreDisplay || formatPercentDisplay(org.averageScore || 0))
          }
        ]
      };

      // Separate charts for organizations - sorted by completion rate (high to low)
      const orgsSortedByCompletion = [...organizationsData].sort((a, b) => (b.completionRate || 0) - (a.completionRate || 0)).slice(0, 10);
      const organizationsCompletionChart = {
        labels: orgsSortedByCompletion.map(org => org.organization || 'Unknown'),
        datasets: [
          {
            label: 'Completion Rate',
            data: orgsSortedByCompletion.map(org => percentValue(org.completionRate)),
            dataLabels: orgsSortedByCompletion.map(org => org.completionRateDisplay || formatPercentDisplay(org.completionRate || 0))
          }
        ]
      };

      // Sorted by average score (high to low)
      const orgsSortedByScore = [...organizationsData].sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0)).slice(0, 10);
      const organizationsScoreChart = {
        labels: orgsSortedByScore.map(org => org.organization || 'Unknown'),
        datasets: [
          {
            label: 'Average Score',
            data: orgsSortedByScore.map(org => percentValue(org.averageScore)),
            dataLabels: orgsSortedByScore.map(org => org.averageScoreDisplay || formatPercentDisplay(org.averageScore || 0))
          }
        ]
      };

      // Department Groups - Completion Rate only (sorted high to low)
      const groupsSortedByCompletion = [...departmentGroupsData].sort((a, b) => (b.completionRate || 0) - (a.completionRate || 0)).slice(0, 10);
      const departmentGroupsChart = {
        labels: groupsSortedByCompletion.map(group => group.label || 'Group'),
        datasets: [
          {
            label: 'Completion Rate',
            data: groupsSortedByCompletion.map(group => percentValue(group.completionRate)),
            dataLabels: groupsSortedByCompletion.map(group => group.completionRateDisplay || formatPercentDisplay(group.completionRate || 0))
          }
        ]
      };

      // Department Groups - 3 Month Comparison Chart (if available)
      // Labels: Department group names (Service, Operations, etc.)
      // Datasets: One per month (November, December, January) - showing AVERAGE SCORE
      const departmentGroupComparisonData = safeAggregates.departmentGroupComparison;
      let departmentGroupComparisonChart = null;
      
      if (departmentGroupComparisonData) {
        const currentStats = departmentGroupComparisonData.currentMonth?.stats || [];
        
        // Labels are the department group names
        const groupLabels = currentStats.map(group => group.label || 'Group');
        
        // Build data arrays for each month - using averageScore
        const month2Data = currentStats.map(group => {
          const stat = (departmentGroupComparisonData.month2?.stats || []).find(s => s.groupId === group.groupId);
          return stat?.averageScore || 0;
        });
        const month1Data = currentStats.map(group => {
          const stat = (departmentGroupComparisonData.month1?.stats || []).find(s => s.groupId === group.groupId);
          return stat?.averageScore || 0;
        });
        const currentData = currentStats.map(group => group.averageScore || 0);
        
        departmentGroupComparisonChart = {
          labels: groupLabels,
          datasets: [
            {
              label: departmentGroupComparisonData.month2?.label || 'Month -2',
              data: month2Data,
              dataLabels: month2Data.map(v => formatPercentDisplay(v))
            },
            {
              label: departmentGroupComparisonData.month1?.label || 'Month -1',
              data: month1Data,
              dataLabels: month1Data.map(v => formatPercentDisplay(v))
            },
            {
              label: departmentGroupComparisonData.currentMonth?.label || 'Current',
              data: currentData,
              dataLabels: currentData.map(v => formatPercentDisplay(v))
            }
          ]
        };
      }

      // Organizations - 3 Month Average Score Comparison Chart (if available)
      // Labels: Organization names
      // Datasets: One per month (November, December, January) - showing AVERAGE SCORE
      const organizationComparisonData = safeAggregates.organizationComparison;
      let organizationsCompletionComparisonChart = null;
      
      if (organizationComparisonData) {
        const currentStats = organizationComparisonData.currentMonth?.stats || [];
        
        // Labels are the organization names
        const orgLabels = currentStats.map(org => org.organization || 'Organization');
        
        // Build data arrays for each month - using averageScore
        const month2Data = currentStats.map(org => {
          const stat = (organizationComparisonData.month2?.stats || []).find(s => s.organization === org.organization);
          return stat?.averageScore || 0;
        });
        const month1Data = currentStats.map(org => {
          const stat = (organizationComparisonData.month1?.stats || []).find(s => s.organization === org.organization);
          return stat?.averageScore || 0;
        });
        const currentData = currentStats.map(org => org.averageScore || 0);
        
        organizationsCompletionComparisonChart = {
          labels: orgLabels,
          datasets: [
            {
              label: organizationComparisonData.month2?.label || 'Month -2',
              data: month2Data,
              dataLabels: month2Data.map(v => formatPercentDisplay(v))
            },
            {
              label: organizationComparisonData.month1?.label || 'Month -1',
              data: month1Data,
              dataLabels: month1Data.map(v => formatPercentDisplay(v))
            },
            {
              label: organizationComparisonData.currentMonth?.label || 'Current',
              data: currentData,
              dataLabels: currentData.map(v => formatPercentDisplay(v))
            }
          ]
        };
      }

      // Department Groups - 3 Month Completion Rate Comparison Chart (if available)
      // Labels: Department group names (Service, Operations, etc.)
      // Datasets: One per month (November, December, January) - showing COMPLETION RATE
      let departmentsCompletionComparisonChart = null;
      
      if (departmentGroupComparisonData) {
        const currentStats = departmentGroupComparisonData.currentMonth?.stats || [];
        
        // Labels are the department group names
        const groupLabels = currentStats.map(group => group.label || 'Group');
        
        // Build data arrays for each month - using completionRate
        const month2Data = currentStats.map(group => {
          const stat = (departmentGroupComparisonData.month2?.stats || []).find(s => s.groupId === group.groupId);
          return stat?.completionRate || 0;
        });
        const month1Data = currentStats.map(group => {
          const stat = (departmentGroupComparisonData.month1?.stats || []).find(s => s.groupId === group.groupId);
          return stat?.completionRate || 0;
        });
        const currentData = currentStats.map(group => group.completionRate || 0);
        
        departmentsCompletionComparisonChart = {
          labels: groupLabels,
          datasets: [
            {
              label: departmentGroupComparisonData.month2?.label || 'Month -2',
              data: month2Data,
              dataLabels: month2Data.map(v => formatPercentDisplay(v))
            },
            {
              label: departmentGroupComparisonData.month1?.label || 'Month -1',
              data: month1Data,
              dataLabels: month1Data.map(v => formatPercentDisplay(v))
            },
            {
              label: departmentGroupComparisonData.currentMonth?.label || 'Current',
              data: currentData,
              dataLabels: currentData.map(v => formatPercentDisplay(v))
            }
          ]
        };
      }

      const topDepartments = departmentsData.slice(0, 10);
      const departmentsChart = {
        labels: topDepartments.map(dept => dept.department || 'Department'),
        datasets: [
          {
            label: 'Response Rate',
            data: topDepartments.map(dept => percentValue(dept.responseRate)),
            dataLabels: topDepartments.map(dept => dept.responseRateDisplay || formatPercentDisplay(dept.responseRate || 0))
          },
          {
            label: 'Completion Rate',
            data: topDepartments.map(dept => percentValue(dept.completionRate)),
            dataLabels: topDepartments.map(dept => dept.completionRateDisplay || formatPercentDisplay(dept.completionRate || 0))
          },
          {
            label: 'Average Score',
            data: topDepartments.map(dept => percentValue(dept.averageScore)),
            dataLabels: topDepartments.map(dept => dept.averageScoreDisplay || formatPercentDisplay(dept.averageScore || 0))
          }
        ]
      };

      // Separate charts for departments - sorted by completion rate (high to low)
      const deptsSortedByCompletion = [...departmentsData].sort((a, b) => (b.completionRate || 0) - (a.completionRate || 0)).slice(0, 10);
      const departmentsCompletionChart = {
        labels: deptsSortedByCompletion.map(dept => dept.department || 'Department'),
        datasets: [
          {
            label: 'Completion Rate',
            data: deptsSortedByCompletion.map(dept => percentValue(dept.completionRate)),
            dataLabels: deptsSortedByCompletion.map(dept => dept.completionRateDisplay || formatPercentDisplay(dept.completionRate || 0))
          }
        ]
      };

      // Sorted by average score (high to low)
      const deptsSortedByScore = [...departmentsData].sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0)).slice(0, 10);
      const departmentsScoreChart = {
        labels: deptsSortedByScore.map(dept => dept.department || 'Department'),
        datasets: [
          {
            label: 'Average Score',
            data: deptsSortedByScore.map(dept => percentValue(dept.averageScore)),
            dataLabels: deptsSortedByScore.map(dept => dept.averageScoreDisplay || formatPercentDisplay(dept.averageScore || 0))
          }
        ]
      };

      // Organization × Group Stats Chart
      const topOrgByGroup = orgByGroupStatsData.slice(0, 15);
      const orgByGroupChart = {
        labels: topOrgByGroup.map(item => `${item.organization} - ${item.group}`),
        datasets: [
          {
            label: 'Completion Rate',
            data: topOrgByGroup.map(item => percentValue(item.completionRate)),
            dataLabels: topOrgByGroup.map(item => item.completionRateDisplay || formatPercentDisplay(item.completionRate || 0))
          },
          {
            label: 'Average Score',
            data: topOrgByGroup.map(item => percentValue(item.averageScore)),
            dataLabels: topOrgByGroup.map(item => item.averageScoreDisplay || formatPercentDisplay(item.averageScore || 0))
          }
        ]
      };

      const payload = {
        reportName: reportTitle,
        overview: overviewData,
        organizations: organizationsData,
        departments: departmentsData,
        departmentGroups: departmentGroupsData,
        departmentGroupComparison: departmentGroupComparisonData,
        organizationComparison: organizationComparisonData,
        departmentComparison: departmentComparisonData,
        groupByOrgStats: groupByOrgStatsData,
        groupByDeptStats: groupByDeptStatsData,
        orgByGroupStats: orgByGroupStatsData,
        groupAnalysis: groupAnalysisData,
        questionsByDeptGroup: questionsByDeptGroupData,
        deptGroupQuestionStats: deptGroupQuestionStatsData,
        questions: safeAggregates.questions || {},
        campaigns: safeAggregates.campaigns || [],
        charts: {
          statusDistribution: statusChart,
          organizations: organizationsChart,
          organizationsCompletion: organizationsCompletionChart,
          organizationsScore: organizationsScoreChart,
          organizationsCompletionComparison: organizationsCompletionComparisonChart, // 3-month org comparison (avg score)
          departmentGroups: departmentGroupsChart,
          departmentGroupsComparison: departmentGroupComparisonChart, // 3-month dept group comparison (avg score)
          departments: departmentsChart,
          departmentsCompletion: departmentsCompletionChart,
          departmentsScore: departmentsScoreChart,
          departmentsCompletionComparison: departmentsCompletionComparisonChart, // 3-month dept comparison (completion rate)
          orgByGroup: orgByGroupChart
        }
      };

      const res = await apiClient.post('/reports/export-pptx', payload, { responseType: 'blob' });
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (reportTitle || 'MBSA Quiz Analysis')
        .replace(/[^a-zA-Z0-9-_\s]/g, '')
        .trim()
        .replace(/\s+/g, '-') || 'MBSA-Quiz-Analysis';
      const now = new Date();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}-${String(now.getMinutes()).padStart(2,'0')}`;
      a.href = url;
      a.download = `${safeName}_${ts}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export DOCX failed', err);
      throw err;
    }
  }, [aggregates, formatNumberDisplay, formatPercentDisplay, percentValue]);

  const handleConfirmExport = useCallback(async () => {
    const trimmed = (reportName || '').trim();
    if (!trimmed) {
      message.warning('Please enter a report name');
      return;
    }

    setExporting(true);
    try {
      await exportDocx(trimmed);
      message.success('Report exported successfully');
      setExportModalOpen(false);
    } catch (err) {
      message.error('Failed to export Word report');
    } finally {
      setExporting(false);
    }
  }, [reportName, exportDocx]);

  const handleExportPdf = useCallback(() => {
    const trimmed = (reportName || '').trim();
    if (!trimmed) {
      message.warning('Please enter a report name');
      return;
    }
    setPdfModalOpen(true);
  }, [reportName]);

  const handleConfirmPdf = useCallback(async () => {
    const trimmed = (reportName || '').trim();
    if (!trimmed) { message.warning('Please enter a report name'); return; }

    setExporting(true);
    try {
      const result = await reportService.exportPdf({
        reportName: trimmed,
        aggregates,
        aiInsights,
        reportLabel: pdfLabel,
        networkName: pdfNetworkName,
        orgLabel: pdfOrgLabel,
        orgLabelPlural: pdfOrgLabelPlural,
        deptLabel: pdfDeptLabel,
        deptLabelPlural: pdfDeptLabelPlural,
        clientLogoUrl: pdfClientLogoUrl,
      });

      if (result?.success) {
        message.success('PDF exported successfully');
        setPdfModalOpen(false);
      } else {
        message.error(result?.message || 'Failed to export PDF');
      }
    } catch (err) {
      message.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  }, [reportName, aggregates, aiInsights, pdfLabel, pdfNetworkName, pdfOrgLabel, pdfOrgLabelPlural, pdfDeptLabel, pdfDeptLabelPlural, pdfClientLogoUrl]);

  const handleSavePdfProfile = useCallback(async () => {
    const name = pdfProfileName.trim();
    if (!name) { message.warning('Please enter a profile name'); return; }
    const profile = { name, reportLabel: pdfLabel, networkName: pdfNetworkName, orgLabel: pdfOrgLabel, orgLabelPlural: pdfOrgLabelPlural, deptLabel: pdfDeptLabel, deptLabelPlural: pdfDeptLabelPlural, reportName, clientLogoUrl: pdfClientLogoUrl };
    const result = await settingsService.savePdfProfile(profile);
    if (result.success) {
      const refreshed = await settingsService.getPdfProfiles();
      setPdfProfiles(Array.isArray(refreshed.data) ? refreshed.data : (result.data || []));
      setPdfProfileName('');
      setSavePdfProfileOpen(false);
      message.success(`Profile "${name}" saved`);
    } else {
      message.error(result.message || 'Failed to save profile');
    }
  }, [pdfProfileName, pdfLabel, pdfNetworkName, pdfOrgLabel, pdfOrgLabelPlural, pdfDeptLabel, pdfDeptLabelPlural, pdfClientLogoUrl]);

  const handleLoadPdfProfile = useCallback((name) => {
    const profile = pdfProfiles.find(p => p.name === name);
    if (!profile) return;
    setPdfLabel(profile.reportLabel || 'National Performance Report');
    setPdfNetworkName(profile.networkName || '');
    setPdfOrgLabel(profile.orgLabel || 'Dealership');
    setPdfOrgLabelPlural(profile.orgLabelPlural || `${profile.orgLabel || 'Dealership'}s`);
    setPdfDeptLabel(profile.deptLabel || 'Department');
    setPdfDeptLabelPlural(profile.deptLabelPlural || `${profile.deptLabel || 'Department'}s`);
    setPdfClientLogoUrl(profile.clientLogoUrl || '');
    if (profile.reportName) setReportName(profile.reportName);
  }, [pdfProfiles]);

  const handleDeletePdfProfile = useCallback(async (name) => {
    const result = await settingsService.deletePdfProfile(name);
    if (result.success) {
      setPdfProfiles(result.data || []);
      message.success(`Profile "${name}" deleted`);
    } else {
      message.error(result.message || 'Failed to delete profile');
    }
  }, []);

  const handleExportCancel = useCallback(() => {
    if (!exporting) {
      setExportModalOpen(false);
    }
  }, [exporting]);
  
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: C.red, padding: 16 }}>{error}</div>;
  }

  if (!aggregates) {
    return <div style={{ color: C.textLight, padding: 16 }}>Select campaigns and click Generate Report to view aggregated data.</div>;
  }

  const overview = aggregates.overview || {};
  const organizations = aggregates.organizations || [];
  const departments = aggregates.departments || [];
  const departmentGroups = aggregates.departmentGroups || [];
  const deptComparison = aggregates.departmentComparison || {};
  const deptGroupComparison = aggregates.departmentGroupComparison || {};

  // Build 3-month trend rows for a dept group — dept level (completion rate + avg score)
  const getTrendRowsForGroup = (dg) => {
    const deptNames = Array.isArray(dg.departments) ? dg.departments : [];
    const m2Stats = (deptComparison.month2 || {}).stats || [];
    const m1Stats = (deptComparison.month1 || {}).stats || [];
    const curStats = (deptComparison.currentMonth || {}).stats || [];
    const allNames = [...new Set([
      ...m2Stats.filter(s => deptNames.length === 0 || deptNames.includes(s.department)).map(s => s.department),
      ...m1Stats.filter(s => deptNames.length === 0 || deptNames.includes(s.department)).map(s => s.department),
      ...curStats.filter(s => deptNames.length === 0 || deptNames.includes(s.department)).map(s => s.department),
    ])];
    return allNames.map(name => {
      const s2 = m2Stats.find(s => s.department === name) || {};
      const s1 = m1Stats.find(s => s.department === name) || {};
      const sc = curStats.find(s => s.department === name) || {};
      return {
        name,
        m2cr: s2.completionRate ?? null, m2sc: s2.averageScore ?? null,
        m1cr: s1.completionRate ?? null, m1sc: s1.averageScore ?? null,
        curcr: sc.completionRate ?? null, cursc: sc.averageScore ?? null,
      };
    });
  };

  const trendMonthLabels = [
    (deptComparison.month2 || {}).label || '',
    (deptComparison.month1 || {}).label || '',
    (deptComparison.currentMonth || {}).label || '',
  ];

  // Build 3-month trend rows for ALL dept groups (overview-level summary table)
  const getGroupTrendRows = () => {
    const m2Stats = (deptGroupComparison.month2 || {}).stats || [];
    const m1Stats = (deptGroupComparison.month1 || {}).stats || [];
    const curStats = (deptGroupComparison.currentMonth || {}).stats || [];
    const baseStats = curStats.length ? curStats : (m1Stats.length ? m1Stats : m2Stats);
    return baseStats.map(s => {
      const s1 = m1Stats.find(x => x.groupId === s.groupId) || {};
      const s2 = m2Stats.find(x => x.groupId === s.groupId) || {};
      const sc = curStats.find(x => x.groupId === s.groupId) || {};
      return {
        name: s.label || s.groupId || '—',
        m2cr: s2.completionRate ?? null, m2sc: s2.averageScore ?? null,
        m1cr: s1.completionRate ?? null, m1sc: s1.averageScore ?? null,
        curcr: sc.completionRate ?? null, cursc: sc.averageScore ?? null,
      };
    });
  };
  const groupTrendMonthLabels = [
    (deptGroupComparison.month2 || {}).label || '',
    (deptGroupComparison.month1 || {}).label || '',
    (deptGroupComparison.currentMonth || {}).label || '',
  ];
  const groupTrendRows = getGroupTrendRows();
  const questions = aggregates.questions || {};
  const allQuestions = questions.analysis || [];
  // Pre-computed per-group questions from backend (deptGroupQuestionStats)
  const deptGroupQuestionStats = Array.isArray(aggregates.deptGroupQuestionStats) ? aggregates.deptGroupQuestionStats : [];
  // Per-org stats broken down by dept group (orgByGroupStats)
  const orgByGroupStats = Array.isArray(aggregates.orgByGroupStats) ? aggregates.orgByGroupStats : [];

  // Get questions for a dept group — strict to pre-computed department-group data
  const getQuestionsForGroup = (dg) => {
    const label = String(dg?.label || '').trim().toLowerCase();
    const entry = deptGroupQuestionStats.find(s => String(s?.groupName || '').trim().toLowerCase() === label);
    if (entry && Array.isArray(entry.questions) && entry.questions.length) {
      return entry.questions.filter((q) => {
        const text = String(q?.questionText || q?.q || '').trim();
        const score = Number(q?.successRate ?? q?.sc);
        return text.length > 0 && Number.isFinite(score);
      });
    }
    return [];
  };

  // Get orgs for a dept group — filter orgByGroupStats by groupId
  const getOrgsForGroup = (dg) => {
    const filtered = orgByGroupStats.filter(o => o.groupId === dg.key || o.groupId === dg.id);
    if (filtered.length) return filtered;
    return organizations; // fallback to all orgs
  };

  // Get department-level rows for a dept group
  const getDepartmentsForGroup = (dg) => {
    const deptNames = Array.isArray(dg.departments) ? dg.departments : [];
    if (deptNames.length > 0) {
      const filtered = departments.filter(d => deptNames.includes(d.department));
      if (filtered.length) return filtered;
    }
    return departments; // fallback
  };

  // Build token-optimised question list for a dept group
  const buildQuestionPayload = (deptQs) =>
    [...(deptQs || [])]
      .sort((a, b) => (a.successRate || 0) - (b.successRate || 0))
      .slice(0, 10)
      .map(q => ({ q: q.questionText || q.q || '', sc: Math.round(q.successRate || q.sc || 0) }));

  // Fire all dept groups in parallel with a single button click
  const generateAllInsights = async () => {
    if (!departmentGroups.length || aiAllLoading) return;
    setAiAllLoading(true);
    // Mark every group as loading immediately so the UI responds
    const loadingState = {};
    departmentGroups.forEach(dg => { loadingState[dg.key || dg.label] = { insight: null, loading: true, err: null }; });
    setAiInsights(loadingState);

    await Promise.all(departmentGroups.map(async (dg) => {
      const key = dg.key || dg.label;
      const deptQs = getQuestionsForGroup(dg);
      const gapCount = deptQs.filter(q => (q.successRate || 0) < 60).length;
      const result = await reportService.getDeptAIInsights({
        departmentName: (dg.label || '').toUpperCase(),
        completionRate: Math.round(dg.completionRate || 0),
        avgScore: Math.round(dg.averageScore || 0),
        staffCount: dg.totalParticipants || 0,
        knowledgeGaps: gapCount,
        questions: buildQuestionPayload(deptQs),
      });
      setAiInsights(prev => ({
        ...prev,
        [key]: {
          insight: result.success ? result.data : null,
          loading: false,
          err: result.success ? null : (result.message || 'Failed'),
        },
      }));
    }));

    setAiAllLoading(false);
  };

  const generateSingleInsight = async (dg) => {
    const key = dg.key || dg.label;
    setAiInsights(prev => ({ ...prev, [key]: { insight: null, loading: true, err: null } }));
    const deptQs = getQuestionsForGroup(dg);
    const gapCount = deptQs.filter(q => (q.successRate || 0) < 60).length;
    const result = await reportService.getDeptAIInsights({
      departmentName: (dg.label || '').toUpperCase(),
      completionRate: Math.round(dg.completionRate || 0),
      avgScore: Math.round(dg.averageScore || 0),
      staffCount: dg.totalParticipants || 0,
      knowledgeGaps: gapCount,
      questions: buildQuestionPayload(deptQs),
    });
    setAiInsights(prev => ({
      ...prev,
      [key]: {
        insight: result.success ? result.data : null,
        loading: false,
        err: result.success ? null : (result.message || 'Failed'),
      },
    }));
  };
  const deptColorMap = {
    sales: C.teal, service: C.navy, parts: C.orange, fi: '#007A88', default: C.teal,
  };
  const getDeptColor = (label = '') => {
    const l = label.toLowerCase();
    if (l.includes('sales')) return deptColorMap.sales;
    if (l.includes('service')) return deptColorMap.service;
    if (l.includes('parts')) return deptColorMap.parts;
    if (l.includes('f&i') || l.includes('f & i') || l.includes('finance')) return deptColorMap.fi;
    return deptColorMap.default;
  };



  // Save PDF profile modal (kept — only modal remaining)
  const savePdfProfileModal = (
    <Modal
      title="Save PDF Profile"
      open={savePdfProfileOpen}
      onOk={handleSavePdfProfile}
      onCancel={() => setSavePdfProfileOpen(false)}
      okText="Save"
      okType="default"
      width={360}
    >
      <Input
        placeholder="Profile name (e.g. MBSA Standard)"
        value={pdfProfileName}
        onChange={(e) => setPdfProfileName(e.target.value)}
      />
      {pdfProfiles.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Existing profiles</div>
          {pdfProfiles.map(p => (
            <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: 12 }}>{p.name}</span>
              <span style={{ fontSize: 11, color: C.red, cursor: 'pointer' }} onClick={() => handleDeletePdfProfile(p.name)}>Delete</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );

  // PDF settings popover content
  const pdfSettingsContent = (
    <div style={{ width: 340, padding: '4px 0' }}>
      {/* File name */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>File name</div>
        <Input
          size="small"
          placeholder="Report name"
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
        />
      </div>

      {/* Profiles */}
      {pdfProfiles.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Load saved profile</div>
          <Select
            size="small"
            style={{ width: '100%' }}
            placeholder="Select a profile…"
            options={pdfProfiles.map(p => ({ value: p.name, label: p.name }))}
            onSelect={handleLoadPdfProfile}
            allowClear
          />
        </div>
      )}

      <Divider style={{ margin: '10px 0', fontSize: 10, color: '#bbb' }}>PDF Labels</Divider>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Cover page label</div>
        <Input size="small" placeholder="National Performance Report" value={pdfLabel} onChange={(e) => setPdfLabel(e.target.value)} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Client / Network name</div>
        <Input size="small" placeholder="e.g. Mercedes-Benz South Africa" value={pdfNetworkName} onChange={(e) => setPdfNetworkName(e.target.value)} />
      </div>

      <Row gutter={8} style={{ marginBottom: 10 }}>
        <Col span={12}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Org label (singular)</div>
          <Input size="small" placeholder="Dealership" value={pdfOrgLabel}
            onChange={(e) => { setPdfOrgLabel(e.target.value); if (!pdfOrgLabelPlural || pdfOrgLabelPlural === `${pdfOrgLabel}s`) setPdfOrgLabelPlural(`${e.target.value}s`); }} />
        </Col>
        <Col span={12}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Plural</div>
          <Input size="small" placeholder="Dealerships" value={pdfOrgLabelPlural} onChange={(e) => setPdfOrgLabelPlural(e.target.value)} />
        </Col>
      </Row>

      <Row gutter={8} style={{ marginBottom: 10 }}>
        <Col span={12}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Dept label (singular)</div>
          <Input size="small" placeholder="Department" value={pdfDeptLabel}
            onChange={(e) => { setPdfDeptLabel(e.target.value); if (!pdfDeptLabelPlural || pdfDeptLabelPlural === `${pdfDeptLabel}s`) setPdfDeptLabelPlural(`${e.target.value}s`); }} />
        </Col>
        <Col span={12}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Plural</div>
          <Input size="small" placeholder="Departments" value={pdfDeptLabelPlural} onChange={(e) => setPdfDeptLabelPlural(e.target.value)} />
        </Col>
      </Row>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Client logo</div>
        {pdfClientLogoUrl ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={pdfClientLogoUrl} alt="logo" style={{ height: 32, maxWidth: 100, objectFit: 'contain', border: '1px solid #eee', borderRadius: 3, padding: 3 }} />
            <span style={{ fontSize: 11, color: C.red, cursor: 'pointer' }} onClick={() => setPdfClientLogoUrl('')}>Remove</span>
          </div>
        ) : (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', fontSize: 11, color: '#555', background: '#fafafa' }}>
            📎 Upload logo
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => setPdfClientLogoUrl(ev.target.result); r.readAsDataURL(f); }} />
          </label>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 11, color: C.teal, cursor: 'pointer' }} onClick={() => setSavePdfProfileOpen(true)}>
          Save as profile…
        </span>
        <button
          onClick={handleConfirmPdf}
          disabled={exporting}
          style={{
            background: exporting ? '#cce8f2' : C.teal, color: exporting ? C.teal : C.white,
            border: 'none', borderRadius: 5, padding: '5px 16px',
            fontSize: 12, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          {exporting ? <Spin size="small" /> : null}
          {exporting ? 'Exporting…' : 'Export PDF'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {savePdfProfileModal}

      {/* Sticky action toolbar — hidden when printing */}
      <div className="no-print" style={{
        position: 'sticky',
        top: 52,
        zIndex: 100,
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${C.border}`,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
        padding: '8px 20px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        borderRadius: 8,
      }}>
        {/* AI status dot */}
        {aiAllLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
            <Spin size="small" />
            <span style={{ fontSize: 11, color: C.navy, fontWeight: 600 }}>Generating insights…</span>
          </div>
        ) : Object.keys(aiInsights).length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 4 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.green }} />
            <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>AI insights ready</span>
          </div>
        ) : null}

        {/* Generate AI Insights */}
        <button
          onClick={generateAllInsights}
          disabled={aiAllLoading}
          style={{
            background: aiAllLoading ? '#e8e8e8' : C.navy,
            color: aiAllLoading ? C.textLight : C.white,
            border: 'none', borderRadius: 6,
            padding: '7px 14px', cursor: aiAllLoading ? 'not-allowed' : 'pointer',
            fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          ✦ {aiAllLoading ? 'Generating…' : 'AI Insights'}
        </button>

        <div style={{ width: 1, height: 22, background: C.border }} />

        {/* Export PDF + settings gear */}
        <div style={{ display: 'flex', alignItems: 'center', borderRadius: 6, overflow: 'hidden', boxShadow: exporting ? 'none' : '0 2px 8px rgba(38,122,150,0.3)' }}>
          <button
            onClick={handleConfirmPdf}
            disabled={exporting}
            style={{
              background: exporting ? '#cce8f2' : C.teal,
              color: exporting ? C.teal : C.white,
              border: 'none', borderRight: `1px solid rgba(255,255,255,0.2)`,
              padding: '7px 16px', cursor: exporting ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {exporting ? <Spin size="small" /> : null}
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
          <Popover
            content={pdfSettingsContent}
            title="PDF Options"
            trigger="click"
            placement="bottomRight"
          >
            <button
              disabled={exporting}
              style={{
                background: exporting ? '#cce8f2' : C.teal,
                color: exporting ? C.teal : C.white,
                border: 'none', padding: '7px 10px',
                cursor: exporting ? 'not-allowed' : 'pointer',
                fontSize: 13, display: 'flex', alignItems: 'center',
              }}
              title="PDF export settings"
            >
              ⚙
            </button>
          </Popover>
        </div>
      </div>

      {/* Report body */}
      <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", color: C.text, background: C.bg, padding: '0 0 40px' }}>

        {/* Page header */}
        <Section>
          <PageHeader
            section="NETWORK REPORT"
            title={reportName}
            subtitle={new Date().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
            onTitleChange={(val) => { setReportName(val); setSaveStatus('idle'); }}
          />
        </Section>

        {/* Network summary */}
        <NetworkSummary overview={overview} orgs={organizations} departmentGroups={departmentGroups} deptLabelPlural={pdfDeptLabelPlural || 'department'} />

        {/* All-groups overview trend table */}
        {groupTrendRows.length > 0 && groupTrendMonthLabels.some(Boolean) && (
          <PerformanceTrend
            deptLabel={`PERFORMANCE BY ${(pdfDeptLabelPlural || 'DEPARTMENTS').toUpperCase()} — OVERVIEW`}
            deptColor={C.teal}
            rows={groupTrendRows}
            months={groupTrendMonthLabels}
            itemLabel={`${pdfDeptLabel || 'Department'} Group`}
            hideLegend
          />
        )}

        {/* Per-department sections */}
        {departmentGroups.map((dg) => {
          const color = getDeptColor(dg.label);
          const deptQs = getQuestionsForGroup(dg);
          const gapCount = deptQs.filter(q => (q.successRate || 0) < 60).length;
          const groupDepts = getDepartmentsForGroup(dg);
          return (
            <div key={dg.id || dg.key} id={`mc-group-${dg.id || dg.key}`} className="report-page-break">
              {/* 3-month performance trend */}
              {trendMonthLabels.some(Boolean) && (
                <PerformanceTrend
                  deptLabel={`${(dg.label || '').toUpperCase()} — PERFORMANCE BY ${(pdfDeptLabel || 'Department').toUpperCase()}`}
                  deptColor={color}
                  rows={getTrendRowsForGroup(dg)}
                  months={trendMonthLabels}
                  itemLabel={pdfDeptLabel || 'Department'}
                />
              )}

              {/* Organisation-level table for this dept group */}
              <DeptDeepDive
                deptLabel={`${(dg.label || '').toUpperCase()} — BY ORGANISATION`}
                deptColor={color}
                completionRate={dg.completionRate}
                avgScore={dg.averageScore}
                staffCount={dg.totalParticipants}
                orgs={getOrgsForGroup(dg)}
                benchmark={90}
              />

              {/* Department drilldown within the group */}
              {groupDepts.length > 0 && (
                <DeptDrilldown
                  deptLabel={`${(dg.label || '').toUpperCase()} — BY ${(pdfDeptLabel || 'Department').toUpperCase()}`}
                  deptColor={color}
                  depts={groupDepts}
                  benchmark={90}
                  itemLabel={pdfDeptLabel || 'department'}
                  itemLabelPlural={pdfDeptLabelPlural || 'departments'}
                />
              )}

              {/* Question Analysis */}
              {deptQs.length > 0 && (
                <QuestionAnalysis
                  deptLabel={`QUESTION ANALYSIS — ${(dg.label || '').toUpperCase()}`}
                  deptColor={color}
                  questions={deptQs}
                />
              )}

              {/* Strategic Summary — populated by global Generate AI Insights button */}
              <StrategicSummary
                deptLabel={(dg.label || '').toUpperCase()}
                deptColor={color}
                insight={aiInsights[dg.key || dg.label]?.insight}
                loading={aiInsights[dg.key || dg.label]?.loading || false}
                err={aiInsights[dg.key || dg.label]?.err}
                month={new Date().toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                onInsightChange={(newInsight) => handleInsightChange(dg.key || dg.label, newInsight)}
                onGenerate={() => generateSingleInsight(dg)}
              />
            </div>
          );
        })}

        {/* Footer */}
        <div style={{
          background: C.teal, color: C.white, borderRadius: 6, padding: '12px 24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8,
        }}>
          <div style={{ fontSize: 11 }}>Confidential — For internal use only</div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>TAPLEARNGO</div>
        </div>
      </div>
    </>
  );
};

export default MultiCampaignReport;
